import {
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { routes } from '@blockpulse3/data/shared';
import { getUserTimezone } from '@blockpulse3/helpers';
import { BrandIcon } from '@blockpulse3/ui/brand-theme';
import { ErrorMessage } from '@blockpulse3/ui/commons';

import { LoginFormDefaults, schema } from './schema';
import { ILoginForm } from './types';

type Props = {
  /* ** Login form default values, can hold email from redirect ** */
  defaultValues?: ILoginForm;
  onSubmit: (data: ILoginForm) => void;
};

export function LoginForm({ defaultValues = LoginFormDefaults, onSubmit }: Props): JSX.Element {
  const i18nLoginForm = useTranslations('LoginForm');

  const { register, formState, handleSubmit } = useForm<ILoginForm>({
    defaultValues,
    resolver: yupResolver(schema),
  });

  /* Styles */
  const bgValue = useBreakpointValue({ base: 'transparent', sm: 'bg-surface' });

  const handleLoginSubmit: SubmitHandler<ILoginForm> = (data: ILoginForm) => {
    data.timezone = getUserTimezone();
    onSubmit(data);
  };

  return (
    <Container bg={bgValue} maxW="lg">
      <Stack spacing="8">
        <Stack align="start" spacing="6">
          <BrandIcon />
          <Heading alignSelf="start" size="lg">
            {i18nLoginForm('FormTitle')}
          </Heading>
        </Stack>
        <form onSubmit={handleSubmit(handleLoginSubmit)}>
          <Stack spacing="6">
            <Stack spacing="4">
              <FormControl isInvalid={!!formState.errors.email}>
                <FormLabel htmlFor="email">{i18nLoginForm('EmailField')}</FormLabel>
                <Input data-cy="email-input" id="email" type="text" {...register('email')} />
                <ErrorMessage error={formState.errors.email} />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="password">{i18nLoginForm('PasswordField')}</FormLabel>
                <Input
                  data-cy="password-input"
                  id="password"
                  isInvalid={!!formState.errors?.password}
                  type="password"
                  {...register('password')}
                />
                <ErrorMessage error={formState.errors.password} />
                <Link data-cy="forgot-password-link" to={routes.forgotPassword.href}>
                  <Text color="secondary" textAlign="end" textDecoration="underline">
                    {i18nLoginForm('ForgotPassword')}
                  </Text>
                </Link>
              </FormControl>
            </Stack>
            <Stack spacing="2">
              <Button data-cy="submit" type="submit">
                {i18nLoginForm('Submit')}
              </Button>
              <Link data-cy="signup-link" to={routes.signup.href}>
                <Text color="secondary" textAlign="center" textDecoration="underline">
                  {i18nLoginForm('SignUp')}
                </Text>
              </Link>
            </Stack>
          </Stack>
        </form>
      </Stack>
    </Container>
  );
}

export type LoginFormProps = Props;
